import React, { useState } from "react";
import { AccountManagement } from "components/AccountManagement/AccountManagement";
import { LogsComponent } from "components/Logs";
import { SettingsPageLayout } from "newui/features/Settings/components/SettingsPageLayout";
import { Company2faPolicySection } from "newui/features/Settings/components/Security/Company2faPolicySection";
import { useHasTwoFactorAuth } from "hooks/featureFlags";
import { useCurrentUser } from "hooks/auth";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import type { Company } from "newui/features/Company/types";

// Legacy Redux-connected components lack TypeScript prop typings — cast to typed wrappers
const AccountManagementTyped = AccountManagement as React.ComponentType<{ company: Company }>;
const LogsComponentTyped = LogsComponent as React.ComponentType<{ company: Company }>;

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface AccountSettingsProps {
  company: Company;
  path?: string;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const PAYMENTANDTRANSACTION = "Plans & Billing";
const LOGS = "Activity Log";
const SECURITY = "Security";

const BASE_TAB_MENU: TabMenuItem[] = [
  {
    id: 'plans-billing',
    name: PAYMENTANDTRANSACTION,
    showBadge: false,
    total: 0,
  },
  {
    id: 'activity-log',
    name: LOGS,
    showBadge: false,
    total: 0,
  },
];

const SECURITY_TAB: TabMenuItem = {
  id: 'security',
  name: SECURITY,
  showBadge: false,
  total: 0,
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const AccountSettings = ({ company }: AccountSettingsProps) => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const tab = params.get("tab");

  const hasTwoFactorAuth = useHasTwoFactorAuth();
  const { currentUser } = useCurrentUser();
  const isCompanyAdmin = Boolean(currentUser?.company_admin);
  // The Security tab now hosts company-wide 2FA policy — admin-only. Per-user 2FA
  // management has moved to the Profile page.
  const showSecurityTab = hasTwoFactorAuth && isCompanyAdmin;
  const tabMenu: TabMenuItem[] = showSecurityTab
    ? [...BASE_TAB_MENU, SECURITY_TAB]
    : BASE_TAB_MENU;

  const [currentTab, setCurrentTab] = useState<string>(tab || PAYMENTANDTRANSACTION);

  const changeActiveTab = (name: string, _id: string): void => {
    setCurrentTab(name);
  };

  return (
    <SettingsPageLayout
      heading="Account Settings"
      tabs={tabMenu}
      activeTab={currentTab}
      onTabChange={changeActiveTab}
    >
      {currentTab === PAYMENTANDTRANSACTION && <AccountManagementTyped company={company} />}
      {currentTab === LOGS && <LogsComponentTyped company={company} />}
      {currentTab === SECURITY && showSecurityTab && <Company2faPolicySection />}
    </SettingsPageLayout>
  );
};
